import React, { useState, useEffect } from 'react';
import { Sparkles, Zap, TrendingUp, Search, ExternalLink, ArrowRight, BrainCircuit, Settings, RefreshCw } from 'lucide-react';
import { aiUsageApi, AIGlobalSummary } from '../lib/api/ai-usage';
import { useNavigate } from 'react-router-dom';

export const AIUsagePage: React.FC = () => {
    const navigate = useNavigate();
    const [summary, setSummary] = useState<AIGlobalSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async (isRefresh = false) => {
        try {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);

            const data = await aiUsageApi.getGlobalSummary();
            setSummary(data);
        } catch (e) {
            console.error('Failed to fetch AI summary', e);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const filteredUsers = summary?.topUsers.filter(u =>
        u.userId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.user.email?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
                        <Sparkles className="text-purple-600" />
                        AI Usage Dashboard
                    </h1>
                    <p className="text-neutral-500 text-sm">Monitor global token consumption and costs across all users.</p>
                </div>
                <button
                    onClick={() => fetchData(true)}
                    disabled={refreshing}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-neutral-200 rounded-lg text-xs font-medium text-neutral-600 hover:bg-neutral-50 transition-colors disabled:opacity-50"
                >
                    <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                    {refreshing ? 'Refreshing...' : 'Refresh Stats'}
                </button>
            </div>

            {/* Global Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Total Tokens (7d)</div>
                    <div className="text-2xl font-bold text-neutral-900">
                        {loading ? '...' : (summary?.thisWeek.totalTokens.toLocaleString() || '0')}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400 flex items-center gap-1">
                        <TrendingUp size={10} className="text-green-500" />
                        Rolling 7-day window
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Total Requests (7d)</div>
                    <div className="text-2xl font-bold text-neutral-900">
                        {loading ? '...' : (summary?.thisWeek.totalRequests.toLocaleString() || '0')}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400">Total API calls to LLMs</div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Estimated Cost (7d)</div>
                    <div className="text-2xl font-bold text-neutral-900 text-emerald-600">
                        {loading ? '...' : `$${summary?.thisWeek.estimatedCostUsd.toFixed(2) || '0.00'}`}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400">Based on model pricing</div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Active AI Users</div>
                    <div className="text-2xl font-bold text-neutral-900">
                        {loading ? '...' : (summary?.topUsers.length || '0')}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400">Users with usage this week</div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Top Users Table */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-neutral-100 bg-neutral-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <h2 className="text-sm font-semibold text-neutral-900">Top Users (Last 7 Days)</h2>
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" size={14} />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by User ID, Name or Email..."
                                className="pl-8 pr-3 py-1.5 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500 w-full sm:w-64"
                            />
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase font-bold border-b border-neutral-100">
                                <tr>
                                    <th className="px-5 py-3">Customer</th>
                                    <th className="px-5 py-3">Requests</th>
                                    <th className="px-5 py-3">Tokens Used</th>
                                    <th className="px-5 py-3 text-right">Est. Cost</th>
                                    <th className="px-5 py-3 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="px-5 py-10 text-center text-neutral-400 text-xs italic">
                                            Loading top users...
                                        </td>
                                    </tr>
                                ) : filteredUsers.length > 0 ? (
                                    filteredUsers.map((item) => (
                                        <tr key={item.userId} className="hover:bg-neutral-50/50 transition-colors group">
                                            <td className="px-5 py-4">
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-neutral-900">{item.user.name || 'Unnamed'}</span>
                                                    <span className="text-[10px] text-neutral-500">{item.user.email}</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 text-neutral-600 font-medium">
                                                {item.requestCount.toLocaleString()}
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-neutral-900 font-semibold">{item.totalTokens.toLocaleString()}</span>
                                                    <div className="hidden group-hover:block px-1.5 py-0.5 rounded bg-purple-100 text-[9px] font-bold text-purple-700">
                                                        {Math.round(item.totalTokens / (summary?.thisWeek.totalTokens || 1) * 100)}%
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 text-right font-medium text-emerald-600">
                                                ${item.estimatedCostUsd.toFixed(2)}
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                <button
                                                    onClick={() => navigate(`/customers/${item.userId}`)}
                                                    className="p-1.5 rounded-lg border border-neutral-100 bg-white text-neutral-400 hover:text-purple-600 hover:border-purple-100 transition-all shadow-sm"
                                                    title="View Customer Details"
                                                >
                                                    <ExternalLink size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td className="px-5 py-10 text-center text-neutral-400 text-xs italic" colSpan={5}>
                                            {searchQuery ? 'No users matching your search.' : 'No AI usage recorded yet.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* AI Configuration Info */}
                <div className="space-y-6">
                    <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl p-5 text-white shadow-lg shadow-purple-200">
                        <BrainCircuit className="mb-4 opacity-80" size={32} />
                        <h3 className="text-lg font-bold mb-2">Quota Architecture</h3>
                        <p className="text-purple-100 text-xs leading-relaxed mb-4">
                            Limits are enforced using a 7-day (168h) rolling window. Total tokens (Prompt + Completion) are measured against the user's plan allowance.
                        </p>
                        <div className="space-y-2 border-t border-white/10 pt-4">
                            <div className="flex justify-between text-[10px] uppercase font-bold tracking-widest opacity-80">
                                <span>Window</span>
                                <span>168 Hours</span>
                            </div>
                            <div className="flex justify-between text-[10px] uppercase font-bold tracking-widest opacity-80">
                                <span>Log Retention</span>
                                <span>90 Days</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-neutral-200 p-5 shadow-sm">
                        <h3 className="text-sm font-semibold text-neutral-900 mb-4">Admin Controls</h3>
                        <div className="space-y-2">
                            <button onClick={() => navigate('/payments/features')} className="w-full flex items-center justify-between p-3 rounded-lg border border-neutral-100 hover:bg-neutral-50 transition-colors text-xs text-neutral-700 font-medium group">
                                <span className="flex items-center gap-3">
                                    <Settings size={14} className="text-neutral-400" />
                                    Configure Quota Limits
                                </span>
                                <ArrowRight size={14} className="text-neutral-300 group-hover:text-purple-500 transition-colors" />
                            </button>
                            <button
                                onClick={() => navigate('/logs')}
                                className="w-full flex items-center justify-between p-3 rounded-lg border border-neutral-100 hover:bg-neutral-50 transition-colors text-xs text-neutral-700 font-medium group"
                            >
                                <span className="flex items-center gap-3">
                                    <Zap size={14} className="text-neutral-400" />
                                    Review Raw Logs
                                </span>
                                <ArrowRight size={14} className="text-neutral-300 group-hover:text-purple-500 transition-colors" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
